import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const ConfirmContext = createContext(null);

const VARIANT_META = {
  danger: {
    icon: Trash2,
    iconWrap: "bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400",
    confirmVariant: "destructive",
  },
  warning: {
    icon: AlertTriangle,
    iconWrap: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
    confirmVariant: "default",
  },
  info: {
    icon: Info,
    iconWrap: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    confirmVariant: "default",
  },
};

function DialogPanel({ state, onConfirm, onCancel }) {
  const titleId = useId();
  const descId = useId();
  const confirmRef = useRef(null);
  const meta = VARIANT_META[state.variant] || VARIANT_META.info;
  const Icon = meta.icon;
  const isAlert = state.type === "alert";

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity"
        onClick={onCancel}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={state.description ? descId : undefined}
        className="relative z-10 w-full max-w-md rounded-xl border border-border bg-background shadow-2xl"
      >
        <div className="p-5 sm:p-6">
          <div className="flex gap-4">
            <div
              className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
                meta.iconWrap
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id={titleId} className="text-lg font-semibold text-foreground leading-tight">
                {state.title}
              </h2>
              {state.description && (
                <p id={descId} className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {state.description}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {!isAlert && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={onCancel}
              >
                {state.cancelLabel}
              </Button>
            )}
            <Button
              ref={confirmRef}
              type="button"
              variant={isAlert ? "default" : meta.confirmVariant}
              className="min-h-11"
              onClick={onConfirm}
            >
              {state.confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setState(null);
  }, []);

  const open = useCallback((options) => {
    return new Promise((resolve) => {
      // Resolve any pending dialog as cancelled
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setState({
        type: options.type || "confirm",
        title: options.title || "Confirm",
        description: options.description || options.message || "",
        confirmLabel: options.confirmLabel || (options.type === "alert" ? "OK" : "Confirm"),
        cancelLabel: options.cancelLabel || "Cancel",
        variant: options.variant || (options.type === "alert" ? "info" : "danger"),
      });
    });
  }, []);

  const confirm = useCallback(
    (options = {}) => {
      if (typeof options === "string") {
        return open({ type: "confirm", title: "Confirm", description: options, variant: "danger" });
      }
      return open({ type: "confirm", variant: "danger", ...options });
    },
    [open]
  );

  const alert = useCallback(
    (options = {}) => {
      if (typeof options === "string") {
        return open({ type: "alert", title: "Notice", description: options, variant: "info" });
      }
      return open({ type: "alert", variant: "info", confirmLabel: "OK", ...options });
    },
    [open]
  );

  const value = { confirm, alert };

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state && (
        <DialogPanel
          state={state}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return ctx;
}
